import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Megaphone, Plus, Heart, ThumbsUp, Target, Award, 
  Trophy, MessageCircle, Trash2, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EOSHeadlines = () => {
  const [headlines, setHeadlines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [selectedHeadline, setSelectedHeadline] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [viewMode, setViewMode] = useState('my'); // 'my' or 'all'

  // Get user info from AuthContext
  const { user: storedUser } = useAuth();
  const isAdmin = storedUser && ['super_admin', 'admin', 'ceo', 'president'].includes(storedUser.role);
  const currentUserId = storedUser?.user_id || storedUser?.id;

  // Form state
  const [headlineForm, setHeadlineForm] = useState({
    message: '',
    category: 'general',
    department: '',
    image_url: '',
    link_url: ''
  });

  useEffect(() => {
    fetchHeadlines();
  }, [categoryFilter, viewMode]);

  const fetchHeadlines = async () => {
    try {
      let url = `${API}/eos/headlines`;
      const params = new URLSearchParams();
      
      if (categoryFilter !== 'all') {
        params.append('category', categoryFilter);
      }
      
      // Filter by author if "My Headlines" mode
      if (viewMode === 'my') {
        params.append('author_id', currentUserId);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await axios.get(url);
      setHeadlines(response.data);
    } catch (error) {
      console.error('Error fetching headlines:', error);
      toast.error('Failed to load headlines');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateHeadline = async () => {
    try {
      if (!headlineForm.message.trim()) {
        toast.error('Please enter a message');
        return;
      }

      await axios.post(`${API}/eos/headlines`, headlineForm);
      toast.success('Headline shared successfully!');
      setCreateDialogOpen(false);
      resetHeadlineForm();
      fetchHeadlines();
    } catch (error) {
      console.error('Error creating headline:', error);
      toast.error(error.response?.data?.detail || 'Failed to share headline');
    }
  };

  const handleReact = async (headlineId, emoji) => {
    try {
      await axios.post(`${API}/eos/headlines/${headlineId}/react`, null, {
        params: { emoji }
      });
      fetchHeadlines();
    } catch (error) {
      console.error('Error reacting to headline:', error);
      toast.error('Failed to add reaction');
    }
  };

  const handleComment = async () => {
    try {
      if (!commentText.trim()) {
        toast.error('Please enter a comment');
        return;
      }

      await axios.post(
        `${API}/eos/headlines/${selectedHeadline.id}/comment`,
        null,
        { params: { text: commentText } }
      );
      
      toast.success('Comment added');
      setCommentDialogOpen(false);
      setCommentText('');
      setSelectedHeadline(null);
      fetchHeadlines();
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Failed to add comment');
    }
  };

  const handleDelete = async (headlineId) => {
    if (!window.confirm('Are you sure you want to delete this headline?')) {
      return;
    }

    try {
      await axios.delete(`${API}/eos/headlines/${headlineId}`);
      toast.success('Headline deleted');
      fetchHeadlines();
    } catch (error) {
      console.error('Error deleting headline:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete headline');
    }
  };

  const resetHeadlineForm = () => {
    setHeadlineForm({
      message: '',
      category: 'general',
      department: '',
      image_url: '',
      link_url: ''
    });
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'customer_win':
        return <Trophy className="h-4 w-4" />;
      case 'milestone':
        return <Target className="h-4 w-4" />;
      case 'recognition':
        return <Award className="h-4 w-4" />;
      case 'rock_complete':
        return <ThumbsUp className="h-4 w-4" />;
      default:
        return <Megaphone className="h-4 w-4" />;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'customer_win':
        return 'bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-300';
      case 'milestone':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300';
      case 'recognition':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-950 dark:text-purple-300';
      case 'rock_complete':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
    }
  };

  const formatCategoryLabel = (category) => {
    return category.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const getReactionCount = (reactions, emoji) => {
    return reactions?.filter(r => r.emoji === emoji).length || 0;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading Headlines...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Megaphone className="h-8 w-8 text-green-600" />
            <div>
              <h1 className="text-3xl font-bold">Headlines</h1>
              <p className="text-sm text-muted-foreground">
                Share and celebrate good news from the team
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* View Mode Toggle */}
            <div className="flex rounded-lg border border-slate-300 dark:border-slate-700 overflow-hidden">
              <button
                onClick={() => setViewMode('my')}
                className={`px-3 py-1.5 text-sm transition-colors ${
                  viewMode === 'my' 
                    ? 'bg-green-600 text-white' 
                    : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                }`}
              >
                My Headlines
              </button>
              {isAdmin && (
                <button
                  onClick={() => setViewMode('all')}
                  className={`px-3 py-1.5 text-sm transition-colors ${
                    viewMode === 'all' 
                      ? 'bg-green-600 text-white' 
                      : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                  }`}
                >
                  All Headlines
                </button>
              )}
            </div>
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Share Good News
            </Button>
          </div>
        </div>

        {/* Category Filter Tabs */}
        <Tabs value={categoryFilter} onValueChange={setCategoryFilter}>
          <TabsList>
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="customer_win">Customer Wins</TabsTrigger>
            <TabsTrigger value="milestone">Milestones</TabsTrigger>
            <TabsTrigger value="recognition">Recognition</TabsTrigger>
            <TabsTrigger value="rock_complete">Rock Completed</TabsTrigger>
            <TabsTrigger value="general">General</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Headlines Feed */}
        <div className="space-y-4">
          {headlines.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <Megaphone className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground mb-4">
                  {viewMode === 'my' ? "You haven't shared any headlines yet!" : "No headlines yet. Be the first to share good news!"}
                </p>
                <Button onClick={() => setCreateDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Share Good News
                </Button>
              </CardContent>
            </Card>
          ) : (
            headlines.map(headline => (
              <Card key={headline.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="space-y-4">
                    {/* Header */}
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className={getCategoryColor(headline.category)}>
                            {getCategoryIcon(headline.category)}
                            <span className="ml-1">{formatCategoryLabel(headline.category)}</span>
                          </Badge>
                          {headline.department && (
                            <Badge variant="outline">{headline.department}</Badge>
                          )}
                        </div>
                        <p className="text-lg font-medium mb-1">{headline.message}</p>
                        <p className="text-sm text-muted-foreground">
                          Shared by {headline.author_name} • {new Date(headline.created_at).toLocaleString()}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(headline.id)}
                      >
                        <Trash2 className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    </div>

                    {/* Image */}
                    {headline.image_url && (
                      <img 
                        src={headline.image_url} 
                        alt="Headline" 
                        className="rounded-lg w-full max-h-96 object-cover"
                      />
                    )}

                    {/* Link */}
                    {headline.link_url && (
                      <a 
                        href={headline.link_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-sm text-blue-600 hover:underline"
                      >
                        <ExternalLink className="h-4 w-4" />
                        View link
                      </a>
                    )}

                    {/* Reactions */}
                    <div className="flex items-center gap-4 pt-2 border-t">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleReact(headline.id, '❤️')}
                        className="gap-1"
                      >
                        ❤️ {getReactionCount(headline.reactions, '❤️')}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleReact(headline.id, '🎉')}
                        className="gap-1"
                      >
                        🎉 {getReactionCount(headline.reactions, '🎉')}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleReact(headline.id, '👍')}
                        className="gap-1"
                      >
                        👍 {getReactionCount(headline.reactions, '👍')}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleReact(headline.id, '🎯')}
                        className="gap-1"
                      >
                        🎯 {getReactionCount(headline.reactions, '🎯')}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedHeadline(headline);
                          setCommentDialogOpen(true);
                        }}
                        className="gap-1 ml-auto"
                      >
                        <MessageCircle className="h-4 w-4" />
                        {headline.comments?.length || 0} Comments
                      </Button>
                    </div>

                    {/* Comments */}
                    {headline.comments && headline.comments.length > 0 && (
                      <div className="space-y-2 pt-2 border-t">
                        {headline.comments.slice(0, 2).map(comment => (
                          <div key={comment.id} className="text-sm">
                            <span className="font-medium">User {comment.user_id.slice(0, 8)}</span>
                            <span className="text-muted-foreground"> • {comment.text}</span>
                          </div>
                        ))}
                        {headline.comments.length > 2 && (
                          <Button
                            variant="link"
                            size="sm"
                            onClick={() => {
                              setSelectedHeadline(headline);
                              setCommentDialogOpen(true);
                            }}
                          >
                            View all {headline.comments.length} comments
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Create Headline Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Share Good News</DialogTitle>
              <DialogDescription>Celebrate wins, milestones, and achievements</DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label>Message *</Label>
                <Textarea
                  placeholder="What's the good news?"
                  rows={4}
                  value={headlineForm.message}
                  onChange={(e) => setHeadlineForm({ ...headlineForm, message: e.target.value })}
                  className="mt-2"
                />
              </div>

              <div>
                <Label>Category</Label>
                <Select
                  value={headlineForm.category}
                  onValueChange={(value) => setHeadlineForm({ ...headlineForm, category: value })}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="customer_win">Customer Win</SelectItem>
                    <SelectItem value="milestone">Milestone</SelectItem>
                    <SelectItem value="recognition">Recognition</SelectItem>
                    <SelectItem value="rock_complete">Rock Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Department (Optional)</Label>
                <Input
                  placeholder="e.g., Sales, Marketing"
                  value={headlineForm.department}
                  onChange={(e) => setHeadlineForm({ ...headlineForm, department: e.target.value })}
                  className="mt-2"
                />
              </div>

              <div>
                <Label>Image URL (Optional)</Label>
                <Input
                  placeholder="https://..."
                  value={headlineForm.image_url}
                  onChange={(e) => setHeadlineForm({ ...headlineForm, image_url: e.target.value })}
                  className="mt-2"
                />
              </div>

              <div>
                <Label>Link URL (Optional)</Label>
                <Input
                  placeholder="https://..."
                  value={headlineForm.link_url}
                  onChange={(e) => setHeadlineForm({ ...headlineForm, link_url: e.target.value })}
                  className="mt-2"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setCreateDialogOpen(false);
                resetHeadlineForm();
              }}>
                Cancel
              </Button>
              <Button onClick={handleCreateHeadline}>Share Headline</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Comments Dialog */}
        <Dialog open={commentDialogOpen} onOpenChange={setCommentDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Comments</DialogTitle>
              <DialogDescription>{selectedHeadline?.message}</DialogDescription>
            </DialogHeader>

            <div className="space-y-4 max-h-96 overflow-y-auto">
              {selectedHeadline?.comments?.map(comment => (
                <div key={comment.id} className="p-3 rounded-lg bg-muted">
                  <p className="text-sm">{comment.text}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {new Date(comment.timestamp).toLocaleString()}
                  </p>
                </div>
              ))}
            </div>

            <div className="space-y-2">
              <Label>Add Comment</Label>
              <Textarea
                placeholder="Write a comment..."
                rows={3}
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
              />
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setCommentDialogOpen(false);
                setCommentText('');
                setSelectedHeadline(null);
              }}>
                Close
              </Button>
              <Button onClick={handleComment}>Post Comment</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default EOSHeadlines;
